import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Script from 'next/script';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { useVertical } from 'lib/Hooks/useVertical';
import { AUTHENTICATED } from 'lib/myNewsConstants';
import { PIANO } from 'lib/brandFeatures';
import { getCookie } from 'lib/cookies';
import { useBedrockRegistration } from 'store';
import { PIANO_URI, getPianoAid, PIANO_ENV } from './pianoConfig';
import PianoWrapper from './PianoWrapper';
import { createPianoEventHandlers } from './pianoEventHandlers';
import { flushTpCommands } from './PianoTagQueue';

const TODAY_LD_FLAG = 'piano-today';
const NEWS_LD_FLAG = 'nbc-news-subscriptions';
const TOKEN_POLL_INTERVAL = 5_000;
const SDK_READY_POLL = 150;

const PianoContext = createContext(null);

/**
 * Custom hook to access the PianoContext
 * @returns {any} The current value of the PianoContext.
 * @throws {Error} If used outside of a PianoContext provider.
 */
export const usePiano = () => useContext(PianoContext);

/**
 * Retrieves the access token from cookies if the code is running in a browser environment.
 * @returns {string|null} The access token if available, or null if running in a non-browser environment.
 */
const getAccessToken = () => (typeof window !== 'undefined' ? getCookie('access_token') : null);

/**
 * PianoProvider injects the Piano JS SDK and keeps its auth state in-sync
 * with Bedrock (both the zustand store and the auth cookie).
 *
 * @param {object}   props
 * @param {ReactNode} props.children  React subtree to wrap
 * @returns {JSX.Element}
 */
// eslint-disable-next-line import/no-default-export
export default function PianoProvider({ children }) {
  const { vertical } = useVertical();
  const flags = useFeatureFlagContext();
  const flagEnabled = flags?.[TODAY_LD_FLAG] || flags?.[NEWS_LD_FLAG];
  const pianoEnabled = !!flagEnabled && getFeatureConfigForBrand(PIANO, vertical);

  const brandAid = useMemo(() => getPianoAid(PIANO_ENV, vertical), [vertical]);
  const scriptSrc = brandAid ? `${PIANO_URI}/xbuilder/experience/load?aid=${brandAid}` : null;

  const authenticationState = useBedrockRegistration((s) => s.authenticationState);
  const showAuthenticationModal = useBedrockRegistration((s) => s.showAuthenticationModal);
  const openAuthModal = useBedrockRegistration((s) => s.setAuthenticationModal);

  const authenticate = useCallback(() => {
    if (
      authenticationState !== AUTHENTICATED
      && !showAuthenticationModal
    ) {
      openAuthModal(true);
    }
  }, [authenticationState, showAuthenticationModal, openAuthModal]);

  const {
    tpPush,
    setUserReference,
    reloadGate,
    startCheckout,
  } = PianoWrapper;


  const [sdkReady, setSdkReady] = useState(false);

  const pendingCheckoutRef = useRef(null);
  const templateShownRef = useRef(false); // suppress early loginRequired
  const userInteractedRef = useRef(false);

  const trackEvent = useCallback((name, props = {}) => {
    tpPush(['track', { name, props }]);
  }, [tpPush]);

  useEffect(() => {
    if (!pianoEnabled) return () => { };
    const id = setInterval(() => {
      if (window?.tp?.push && window.tp.offer && window.tp.experience) {
        clearInterval(id);
        setSdkReady(true);
      }
    }, SDK_READY_POLL);
    return () => clearInterval(id);
  }, [pianoEnabled]);

  const eventHandlers = useMemo(() => createPianoEventHandlers(
    {},
    {
      authenticate,
      trackEvent,
      pendingCheckoutRef,
      templateShownRef,
      userInteractedRef,
      startCheckout,
    },
  ), [authenticate, trackEvent, startCheckout]);

  const handlersRegistered = useRef(false);
  useEffect(() => {
    if (!pianoEnabled || !sdkReady || handlersRegistered.current) return;

    /* checkout life-cycle */
    tpPush(['addHandler', 'startCheckout', eventHandlers.handleCheckoutStart]);
    tpPush(['addHandler', 'checkoutComplete', eventHandlers.handleCheckoutComplete]);
    tpPush(['addHandler', 'checkoutClose', eventHandlers.handleCheckoutClose]);
    tpPush(['addHandler', 'checkoutCustomEvent', eventHandlers.handleCheckoutCustomEvent]);
    tpPush(['addHandler', 'checkoutStateChange', eventHandlers.handleCheckoutStateChange]);

    /* auth */
    tpPush(['addHandler', 'loginRequired', eventHandlers.handleLoginRequired]);
    tpPush(['addHandler', 'loginSuccess', eventHandlers.handleLoginSuccess]);

    /* composer / template */
    tpPush(['addHandler', 'showTemplate', eventHandlers.handleShowTemplate]);
    tpPush(['addHandler', 'showOffer', eventHandlers.handleShowOffer]);
    tpPush(['addHandler', 'experienceExecute', eventHandlers.handleExperienceExecute]);

    flushTpCommands(tpPush);

    /* init after all handlers are wired */
    tpPush(['init']);

    handlersRegistered.current = true;
  }, [pianoEnabled, sdkReady, tpPush, eventHandlers]);

  const syncAuthState = useCallback(
    (accessToken) => {
      if (!pianoEnabled) return;

      setUserReference(accessToken || null);
      if (accessToken) tpPush(['init']);
      reloadGate();
    },
    [pianoEnabled, tpPush, setUserReference, reloadGate],
  );

  /* zustand → Piano bridge */
  const bedrockToken = useBedrockRegistration((s) => s.access_token);
  useEffect(() => {
    if (pianoEnabled) {
      syncAuthState(bedrockToken);
    }
  }, [pianoEnabled, bedrockToken, syncAuthState]);

  /* cookie poll fallback (legacy pages) */
  const accessTokenRef = useRef(getAccessToken());
  useEffect(() => {
    if (!pianoEnabled) return () => { };
    const id = setInterval(() => {
      const current = getAccessToken();
      if (current !== accessTokenRef.current) {
        accessTokenRef.current = current;
        syncAuthState(current);
      }
    }, TOKEN_POLL_INTERVAL);
    return () => clearInterval(id);
  }, [pianoEnabled, syncAuthState]);

  // resume the checkout flow if the user is authenticated
  useEffect(() => {
    if (
      authenticationState !== AUTHENTICATED
      || !pendingCheckoutRef.current
      || !sdkReady
    ) return;

    setTimeout(() => {
      tpPush(['customEvent', { eventName: 'bedrock-auth-toggle', isShown: false }]); // hide piano modal
      startCheckout(pendingCheckoutRef.current);
      pendingCheckoutRef.current = null;
    }, 300);
  }, [authenticationState, sdkReady, startCheckout]);

  const api = useMemo(
    () => Object.freeze({
      pianoEnabled,
      push: tpPush,
      setUserRef: setUserReference,
      trackEvent,
      authenticate,
    }),
    [pianoEnabled, tpPush, setUserReference, trackEvent, authenticate],
  );

  return (
    <>
      {pianoEnabled && scriptSrc && (
        <>
          <Script
            id="piano-stub"
            strategy="beforeInteractive"
            dangerouslySetInnerHTML={{ __html: 'window.tp = window.tp || [];' }}
          />
          <Script
            id="piano-runtime"
            strategy="beforeInteractive"
            src={scriptSrc}
          />
        </>
      )}

      <PianoContext.Provider value={api}>{children}</PianoContext.Provider>
    </>
  );
}
